import React, { useState } from 'react';
import { NameEntry } from '../components/NameEntry';
import { BruteScoreEntry } from '../components/BruteScoreEntry';
import { SwipeTest } from '../components/SwipeTest';
import { ResultsScreen } from '../components/ResultsScreen';
import { Button } from '../components/ui/Button';
import { TestStep, TestResults } from '../types';
import { calculateNerdScore } from '../utils/scoring';
import { useLeaderboard } from '../hooks/useLeaderboard';
import { BrainCircuitIcon, ArrowLeftIcon } from 'lucide-react';
import { sendToWebhooks } from '../hooks/useWebhooks';
interface TestFlowProps {
  onComplete: () => void;
  onHome: () => void;
  onViewLeaderboard: (name: string) => void;
}
export function TestFlow({
  onComplete,
  onHome,
  onViewLeaderboard
}: TestFlowProps) {
  const [step, setStep] = useState<TestStep>('name');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [photo, setPhoto] = useState('');
  const [bruteScore, setBruteScore] = useState(0);
  const [bruteEntryId, setBruteEntryId] = useState<string | null>(null);
  const [swipeCorrect, setSwipeCorrect] = useState(0);
  const [swipeTotal, setSwipeTotal] = useState(0);
  const [nerdTimeSeconds, setNerdTimeSeconds] = useState(0);
  const {
    addEntry,
    updateEntry
  } = useLeaderboard();
  const fullName = `${firstName} ${lastName}`.trim();
  const handleNameSubmit = (fn: string, ln: string, p: string) => {
    setFirstName(fn);
    setLastName(ln);
    setPhoto(p);
    setStep('brute');
  };
  const handleBruteSubmit = (score: number) => {
    setBruteScore(score);
    if (bruteEntryId) {
      updateEntry(bruteEntryId, {
        score,
        name: fullName,
        firstName,
        lastName,
        details: {
          grip: score
        }
      });
    } else {
      const id = addEntry({
        name: fullName,
        firstName,
        lastName,
        score,
        type: 'brawn',
        details: {
          grip: score
        }
      });
      setBruteEntryId(id);
    }
    setStep('nerd-intro');
  };
  const handleSwipeComplete = (correct: number, total: number, timeSeconds: number) => {
    setSwipeCorrect(correct);
    setSwipeTotal(total);
    setNerdTimeSeconds(timeSeconds);
    setStep('calculating');
    setTimeout(async () => {
      const finalNerdScore = calculateNerdScore(timeSeconds);
      addEntry({
        name: fullName,
        firstName,
        lastName,
        score: finalNerdScore,
        type: 'brains',
        details: {
          swipe: correct,
          swipeTotal: total,
          nerdTimeSeconds: timeSeconds
        }
      });
      // Fire webhook with all data
      try {
        await sendToWebhooks({
          firstName,
          lastName,
          fullName,
          photo: photo || null,
          bruteScore,
          nerdTimeSeconds: timeSeconds,
          swipeCorrect: correct,
          swipeTotal: total,
          timestamp: new Date().toISOString()
        });
      } catch (e) {
        // Webhook failure is non-blocking
      }
      setStep('results');
    }, 1500);
  };
  const BackButton = ({
    onClick
  }: {
    onClick: () => void;
  }) => <button onClick={onClick} className="flex items-center gap-2 text-gray-500 hover:text-white transition-colors text-sm mb-6" data-id="element-478">
      <ArrowLeftIcon className="w-4 h-4" data-id="element-479" /> Back
    </button>;
  if (step === 'name') {
    return <div className="min-h-screen bg-zinc-950 p-4 pt-8" data-id="element-480">
        <BackButton onClick={onHome} data-id="element-481" />
        <NameEntry onNext={handleNameSubmit} data-id="element-482" />
      </div>;
  }
  if (step === 'brute') {
    return <div className="relative" data-id="element-483">
        <div className="absolute top-4 left-4 z-10" data-id="element-484">
          <BackButton onClick={() => setStep('name')} data-id="element-485" />
        </div>
        <BruteScoreEntry onNext={handleBruteSubmit} initialScore={bruteScore > 0 ? bruteScore : undefined} data-id="element-486" />
      </div>;
  }
  if (step === 'nerd-intro') {
    return <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center p-4 text-center relative" data-id="element-487">
        <div className="absolute top-4 left-4" data-id="element-488">
          <BackButton onClick={() => setStep('brute')} data-id="element-489" />
        </div>
        <BrainCircuitIcon className="w-24 h-24 text-cyan-500 mb-6 animate-pulse" data-id="element-490" />
        <h1 className="text-5xl md:text-7xl font-mono font-bold text-white mb-6" data-id="element-491">
          NERD TEST <span className="text-cyan-500" data-id="element-492">INITIATED</span>
        </h1>
        <div className="max-w-xl text-gray-400 mb-12 space-y-4 text-lg" data-id="element-493">
          <p data-id="element-494">
            8 business scenarios.{' '}
            <span className="text-white font-bold" data-id="element-495">
              Your time starts when you tap Begin.
            </span>
          </p>
          <p className="font-mono text-cyan-200" data-id="element-496">
            Keep or Toss — trust your gut. Fastest wins.
          </p>
        </div>
        <Button onClick={() => setStep('swipe')} variant="brains" size="xl" className="animate-bounce" data-id="element-497">
          BEGIN
        </Button>
      </div>;
  }
  if (step === 'swipe') {
    return <div className="min-h-screen bg-zinc-950 p-4 pt-6 flex flex-col" data-id="element-498">
        <SwipeTest onComplete={handleSwipeComplete} data-id="element-499" />
      </div>;
  }
  if (step === 'calculating') {
    return <div className="min-h-screen bg-zinc-950 flex items-center justify-center" data-id="element-500">
        <div className="text-center" data-id="element-501">
          <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" data-id="element-502" />
          <h2 className="text-2xl font-mono text-cyan-500 animate-pulse" data-id="element-503">
            CALCULATING SCORES...
          </h2>
        </div>
      </div>;
  }
  if (step === 'results') {
    const results: TestResults = {
      firstName,
      lastName,
      photo,
      bruteScore,
      swipeCorrect,
      swipeTotal,
      nerdTimeSeconds,
      totalNerdScore: calculateNerdScore(nerdTimeSeconds)
    };
    return <ResultsScreen results={results} onHome={onHome} onViewLeaderboard={() => onViewLeaderboard(fullName)} onChallengeAgain={() => {
      setStep('name');
      setBruteScore(0);
      setBruteEntryId(null);
      setSwipeCorrect(0);
      setSwipeTotal(0);
      setNerdTimeSeconds(0);
    }} data-id="element-504" />;
  }
  return null;
}